/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { forwardRef, type ComponentProps, useCallback, useMemo } from 'react';
import { Link } from '@/components/link';

import type { ShopperSearch } from '@/scapi';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { createProductUrl } from '@/lib/product/product-utils';
import { readHitModelCode, readHitModelNameForWeb } from '@/lib/product/custom-attributes';
import { useProductTileContext } from './context';
import { PickupIcon } from '@/components/icons';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import type { ComponentType } from '@/components/region';
import { ProductImageContainer } from '@/components/product-image';
import ProductPrice from '@/components/product-price';
import { Typography } from '@/components/typography';
import { UITarget } from '@/targets/ui-target';
import { Card } from '@/components/ui/card';
import { loader as loaders } from './loaders';

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('productTile', {
    name: 'Product Tile',
    description: 'Configurable product tile with customizable styling for images, typography, and hover effects',
    group: 'Content',
})
@RegionDefinition([])
export class ProductTileMetadata {
    @AttributeDefinition({
        id: 'productId',
        name: 'Product',
        description: 'Select a product to render in this tile.',
        type: 'product',
    })
    productId?: string;

    @AttributeDefinition({
        id: 'objectFit',
        name: 'Image Object Fit',
        description: 'How the product image should fit within its container',
        type: 'enum',
        values: ['contain', 'cover', 'fill', 'none', 'scale-down'],
        defaultValue: 'contain',
    })
    objectFit?: string;

    @AttributeDefinition({
        id: 'borderRadius',
        name: 'Border Radius',
        description: 'Corner roundness of the tile card',
        type: 'enum',
        values: ['none', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', 'full'],
        defaultValue: 'xl',
    })
    borderRadius?: string;

    @AttributeDefinition({
        id: 'boxShadow',
        name: 'Box Shadow',
        description: 'Shadow effect for the tile card',
        type: 'enum',
        values: ['none', 'sm', 'md', 'lg', 'xl', '2xl'],
        defaultValue: 'sm',
    })
    boxShadow?: string;

    @AttributeDefinition({
        id: 'padding',
        name: 'Padding',
        description: 'Padding on all sides of the tile',
        type: 'enum',
        values: ['0', '2', '4', '6', '8'],
        defaultValue: '0',
    })
    padding?: string;

    @AttributeDefinition({
        id: 'margin',
        name: 'Margin',
        description: 'Margin on all sides of the tile',
        type: 'enum',
        values: ['0', '2', '4', '6', '8'],
        defaultValue: '0',
    })
    margin?: string;

    @AttributeDefinition({
        id: 'fontWeight',
        name: 'Font Weight',
        description: 'Weight of the product name text',
        type: 'enum',
        values: ['normal', 'medium', 'semibold', 'bold'],
        defaultValue: 'semibold',
    })
    fontWeight?: string;

    @AttributeDefinition({
        id: 'letterSpacing',
        name: 'Letter Spacing',
        description: 'Spacing between letters in product name',
        type: 'enum',
        values: ['tighter', 'tight', 'normal', 'wide', 'wider'],
        defaultValue: 'normal',
    })
    letterSpacing?: string;

    @AttributeDefinition({
        id: 'hoverEffect',
        name: 'Hover Effect',
        description: 'Interactive hover effect for the tile',
        type: 'enum',
        values: ['default', 'scale', 'shadow', 'lift'],
        defaultValue: 'default',
    })
    hoverEffect?: string;
}
/* v8 ignore stop */

const getPageDesignerStyleClasses = ({
    objectFit,
    borderRadius,
    boxShadow,
    padding,
    margin,
    fontWeight,
    letterSpacing,
    hoverEffect,
}: Partial<ProductTileProps>) => {
    const classes: string[] = [];

    if (objectFit) {
        const fitMap = {
            contain: '[&_img]:!object-contain',
            cover: '[&_img]:!object-cover',
            fill: '[&_img]:!object-fill',
            none: '[&_img]:!object-none',
            'scale-down': '[&_img]:!object-scale-down',
        };
        classes.push(fitMap[objectFit]);
    }

    if (borderRadius) {
        const radiusMap: Record<string, string> = {
            none: '!rounded-none',
            xs: '!rounded-xs',
            sm: '!rounded-sm',
            md: '!rounded-md',
            lg: '!rounded-lg',
            xl: '!rounded-xl',
            '2xl': '!rounded-2xl',
            '3xl': '!rounded-3xl',
            '4xl': '!rounded-4xl',
            full: '!rounded-full',
        };
        classes.push(radiusMap[borderRadius] || '!rounded-none');
    }

    if (boxShadow === 'none') {
        classes.push('!shadow-none hover:!shadow-none');
    } else if (boxShadow) {
        const shadowMap = {
            sm: '!shadow-sm hover:!shadow-sm',
            md: '!shadow-md hover:!shadow-md',
            lg: '!shadow-lg hover:!shadow-lg',
            xl: '!shadow-xl hover:!shadow-xl',
            '2xl': '!shadow-2xl hover:!shadow-2xl',
        };
        classes.push(shadowMap[boxShadow]);
    }

    if (padding && padding !== '0') {
        classes.push(`p-${padding}`);
    }

    if (margin && margin !== '0') {
        classes.push(`m-${margin}`);
    }

    if (fontWeight) {
        const weightMap = {
            normal: '[&_a]:!font-normal',
            medium: '[&_a]:!font-medium',
            semibold: '[&_a]:!font-semibold',
            bold: '[&_a]:!font-bold',
        };
        classes.push(weightMap[fontWeight]);
    }

    if (letterSpacing) {
        const spacingMap = {
            tighter: '[&_a]:!tracking-tighter',
            tight: '[&_a]:!tracking-tight',
            normal: '[&_a]:!tracking-normal',
            wide: '[&_a]:!tracking-wide',
            wider: '[&_a]:!tracking-wider',
        };
        classes.push(spacingMap[letterSpacing]);
    }

    if (hoverEffect && hoverEffect !== 'default') {
        const hoverMap = {
            scale: 'hover:!scale-105 !transition-transform !duration-200 hover:!shadow-md',
            shadow: 'hover:!shadow-xl !transition-shadow !duration-200',
            lift: 'hover:!-translate-y-1 hover:!shadow-lg !transition-all !duration-200',
        };
        classes.push(hoverMap[hoverEffect]);
    }

    return classes.join(' ');
};

export interface ProductTileProps extends ComponentProps<'div'> {
    product?: ShopperSearch.schemas['ProductSearchHit'];
    productId?: string;
    handleProductClick?: (product: ShopperSearch.schemas['ProductSearchHit']) => void;
    /**
     * Image aspect ratio (width/height). This is the tile's only size axis — the Figma
     * `Size3` / `Medium` / `Large` variants are three media proportions on one 400px
     * frame (2.67:1, 2:1, 1.33:1), not three components, so a consumer that wants
     * shorter or taller media passes a ratio rather than selecting a variant.
     * Defaults to `config.global.productListing.defaultProductTileImgAspectRatio`.
     */
    imgAspectRatio?: number;
    /** Show the pickup available indicator icon with tooltip */
    showPickupAvailable?: boolean;
    /**
     * Dealer pricing tier displayed beside the price (for example, "Bronze").
     * Tier data is not currently available; the storefront only supports list and
     * sale price books (see CRCB2B-114, CRCB2B-115, and CRCB2B-118).
     *
     * When omitted, no tier label is rendered, preventing placeholder or inaccurate
     * pricing information from reaching shoppers. Once tier data is available, a
     * loader or Page Designer attribute can supply this value.
     */
    dealerTier?: string;
    /** Accepted for API compatibility; has no effect */
    showNavigationArrows?: boolean;

    // Page Designer styling props
    objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
    borderRadius?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full';
    boxShadow?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    padding?: '0' | '2' | '4' | '6' | '8';
    margin?: '0' | '2' | '4' | '6' | '8';
    fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
    letterSpacing?: 'tighter' | 'tight' | 'normal' | 'wide' | 'wider';
    hoverEffect?: 'default' | 'scale' | 'shadow' | 'lift';

    // Page Designer system props (need to be filtered)
    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

const ProductTile = forwardRef<HTMLDivElement, ProductTileProps>(
    (
        {
            className,
            product: productProp,
            productId: _productId,
            handleProductClick,
            imgAspectRatio,
            showPickupAvailable = false,
            dealerTier,
            showNavigationArrows: _showNavigationArrows,
            // Page Designer styling props
            objectFit,
            borderRadius,
            boxShadow,
            padding,
            margin,
            fontWeight,
            letterSpacing,
            hoverEffect,
            // Page Designer system props (filter out)
            regionId: _regionId,
            component: _component,
            componentData: _componentData,
            designMetadata: _designMetadata,
            data,
            ...props
        },
        ref
    ) => {
        // Prioritize loader data (Page Designer) over prop (programmatic use)
        const product = (data as ShopperSearch.schemas['ProductSearchHit'] | undefined) || productProp;

        const { config, t, currency, getBadges } = useProductTileContext();

        const productData = useMemo(() => {
            if (!product) return null;
            return {
                badges: getBadges(product),
            };
        }, [product, getBadges]);

        const effectiveImgAspectRatio = imgAspectRatio ?? config.global.productListing.defaultProductTileImgAspectRatio;

        const isMasterProd = !!product?.variants;
        const isBundleOrSet = product?.productType?.bundle || product?.productType?.set;
        const defaultVariantPid = isMasterProd && !isBundleOrSet ? (product?.representedProduct?.id ?? null) : null;

        const handleClick = useCallback(() => {
            product && handleProductClick?.(product);
        }, [handleProductClick, product]);

        // `productId`, NOT the model code below — the model code is a display string, and only the
        // SKU resolves to a PDP.
        const productUrl = createProductUrl(product?.productId ?? '', null, 'color', defaultVariantPid);
        // CRCB2B-385: master-level merchant attributes for display, falling back to the typed
        // fields when the catalogue leaves them unset so an absent attribute never renders a blank.
        const productName = readHitModelNameForWeb(product) ?? product?.productName ?? '';
        const modelCode = readHitModelCode(product) ?? product?.productId;

        const pageDesignerStyles = getPageDesignerStyleClasses({
            objectFit,
            borderRadius,
            boxShadow,
            padding,
            margin,
            fontWeight,
            letterSpacing,
            hoverEffect,
        });

        if (!product) {
            return (
                <Card
                    ref={ref}
                    className={cn(
                        'product-card group w-full min-w-0 max-w-full overflow-hidden gap-0 py-0',
                        pageDesignerStyles,
                        className
                    )}
                    {...props}>
                    <div className="p-4 text-sm text-muted-foreground">{t('selectProduct')}</div>
                </Card>
            );
        }

        return (
            <Card
                ref={ref}
                className={cn(
                    'product-card group w-full min-w-0 max-w-full cursor-pointer overflow-hidden gap-0 py-0',
                    pageDesignerStyles,
                    className
                )}
                {...props}>
                {/* Image area */}
                <div className="product-image relative">
                    <div className="relative w-full overflow-hidden">
                        {/* CRCB2B-100: primary image only. The AC is explicit ("product image
                            (primary image only)"), and the swatch affordance that used to switch
                            this image to a variant's is gone, so there is nothing to select.

                            `object-contain`, not cover: the design sets the media on a grey
                            surface and letterboxes the product inside it. At the 2:1 proportion
                            cover would crop a squarish product photo top and bottom, which on a
                            catalogue of cranksets and derailleurs loses the subject. No
                            `aspect-square` here either — the ratio drives the box now. */}
                        <ProductImageContainer
                            product={product}
                            selectedColorValue={null}
                            imgAspectRatio={effectiveImgAspectRatio}
                            className="w-full [&_img]:object-contain [&_img]:h-full! [&_img]:max-w-full! [&_img]:mx-auto!"
                            handleProductClick={handleProductClick}
                            // Shares the resolved name, so this link's `View …` label and the
                            // overlay's below name the product identically.
                            displayName={productName}
                        />
                        <UITarget targetId="sfcc.plp.shipping.deliveryEstimate" />

                        {/* Clickable product link overlay */}
                        <Link
                            to={productUrl}
                            className="absolute inset-0 z-[1] cursor-pointer"
                            aria-label={`View ${productName}`}
                            onClick={handleClick}
                            tabIndex={-1}
                        />

                        {/* Badges — top-left */}
                        {productData?.badges.hasBadges && (
                            <div className="absolute top-2 left-2 flex flex-col items-start gap-1 z-20">
                                {/* CRCB2B-640: was a hand-rolled <span data-slot="badge">
                                    on `bg-foreground text-background`. Neutral/Default is
                                    the token pairing for that (fill-neutral #141414 with
                                    white), and matches the promotional treatment the
                                    ProductBadges wrapper now resolves to. `uppercase` is
                                    kept — it is this tile's own content convention. */}
                                {productData.badges.badges.map((badge) => (
                                    <Badge key={badge.label} variant="neutral" className="uppercase">
                                        {badge.label}
                                    </Badge>
                                ))}
                            </div>
                        )}

                        {/* Action icons — top-right */}
                        <div className="absolute top-2 right-2 flex flex-col items-end gap-2 z-20">
                            {showPickupAvailable && (
                                <div>
                                    <div className="group/pickup relative" data-testid="pickup-available-indicator">
                                        <div className="w-9 h-9 p-2 bg-muted text-muted-foreground flex items-center justify-center">
                                            <PickupIcon className="w-4 h-4" />
                                        </div>
                                        <div className="absolute right-0 top-full mt-1 z-50 opacity-0 group-hover/pickup:opacity-100 transition-opacity duration-200 pointer-events-none">
                                            <div className="bg-foreground text-background text-xs font-medium px-2 py-1 whitespace-nowrap shadow-lg">
                                                {t('pickupAvailable')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* CRCB2B-100: no quick-add and no wishlist affordance, on any surface.
                            SRAM's catalogue is browsed at the model level and a model is not
                            addable to a cart or a wishlist — true of carousels and Page Designer
                            regions as much as the listing, so this is the component's shape
                            rather than a per-surface variant (confirmed tile-wide 2026-08-06).
                            The hover tint and the group-hover reveal scaffolding left with them. */}
                    </div>
                </div>

                {/* Info section — Figma `1503:25490`. Four fields only: media (above), name,
                    model number, price. Flush to the tile edge with no horizontal padding, and
                    an 8px gap below the media, per the design's `Info Container` at x=0. The
                    brand line, top-category line, colour swatches and star rating are removed
                    rather than hidden — a hidden node still costs hydration and still reaches
                    assistive technology. */}
                <div className="relative flex flex-col gap-1 pt-2">
                    {/* Product name — the single keyboard/SR tab stop for this tile.
                        Figma renders this as a <p>; the heading is kept because it is the
                        correct document outline for a tile and the tile's only tab stop. */}
                    <Typography as="h3" variant="large" className="text-foreground">
                        <Link
                            to={productUrl}
                            className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                            onClick={handleClick}>
                            {productName}
                        </Link>
                    </Typography>

                    <div className="flex flex-col gap-0.5">
                        {/* Model number — master `c_model-code`, falling back to `productId`
                            (CRCB2B-385). The `SKU ` prefix the design does not show is gone. */}
                        {modelCode && (
                            <Typography
                                as="p"
                                variant="small"
                                className="text-muted-foreground"
                                data-testid="product-tile-sku">
                                {modelCode}
                            </Typography>
                        )}

                        {/* Price and its pricing-tier label, baseline-aligned per the design's
                            `items-end` row. ProductPrice still renders a min–max range for a
                            master with more than one variant, which the AC requires. */}
                        {/* Price and its pricing-tier label, baseline-aligned per the design's
                            `items-end` row. Both render SRAM's body weight (450) with no local
                            override: CurrentPrice's hardcoded `font-semibold` was removed at
                            source, so Typography's variant weight now reaches the price. */}
                        {/* `labelForA11y` uses the resolved name, matching the heading. `||` not
                            `??` — `productName` is `''`-defaulted above, so `??` would never reach
                            the `productId` fallback. */}
                        <div className="flex items-end gap-2">
                            <ProductPrice
                                type="unit"
                                product={product}
                                currency={currency ?? config.commerce.sites?.[0]?.defaultCurrency ?? ''}
                                labelForA11y={productName || product?.productId || ''}
                                currentPriceProps={{
                                    className: 'text-foreground',
                                }}
                                listPriceProps={{
                                    // Sale/list price = body/sm on --text-color-secondary,
                                    // right-aligned. Only the alignment is passed: ListPrice
                                    // already renders `Typography variant="small"` with
                                    // `line-through text-muted-foreground`, which supplies 14px /
                                    // 450 / 1.5 (21px) / 0.03em and the secondary colour exactly.
                                    // The previous override here passed `leading-none`, which
                                    // tailwind-merge resolved OVER the variant's `leading-normal`
                                    // and collapsed the line box from 21px to 16px.
                                    className: 'text-right',
                                }}
                                promoCalloutProps={{
                                    className: 'text-xs text-active-foreground mt-1',
                                }}
                                className="text-sm"
                            />
                            {/* Pricing-tier label (design shows "$555 Bronze price"). Rendered only
                                when the `dealerTier` prop is supplied — no live surface passes one
                                today (the site has no tier data; CRCB2B-114 / CRCB2B-115 /
                                CRCB2B-118), so the slot is reserved but empty rather than showing a
                                hardcoded "Bronze price" to every shopper. Wiring real tiers is then
                                a data change: a loader / Page Designer attribute feeds the prop and
                                the label is templated, so no markup moves.

                                decision 2026-08-07 (MR !38 review): the label was previously
                                unconditional. The translation key alone made substituting real tier
                                names a data change, but it did not stop the placeholder shipping to
                                every shopper on the PLP, search and home carousels. Gating on the
                                prop keeps the integration point and drops the placeholder.

                                `text-placeholder` is the grey-600 ramp step, which is Figma's
                                --text-color-tertiary (#676767). The token is named for its most
                                common use, not exclusively for placeholders; --muted-foreground
                                is grey-700 (#585858) and one step too dark here. */}
                            {dealerTier && (
                                <Typography as="span" variant="small" className="text-placeholder">
                                    {t('dealerTierPrice', { tier: dealerTier })}
                                </Typography>
                            )}
                        </div>
                    </div>

                    <UITarget targetId="sfcc.productCard.reviews.rating" />
                    <UITarget targetId="sfcc.productCard.loyalty.points" />
                    <UITarget targetId="sfcc.productCard.bnpl.message" />
                </div>
            </Card>
        );
    }
);

ProductTile.displayName = 'ProductTile';

// eslint-disable-next-line react-refresh/only-export-components
export const loader = loaders.server;
export { ProductTile };
// eslint-disable-next-line react-refresh/only-export-components
export { ProductTileProvider, useProductTileContext } from './context';
export default ProductTile;
